import React, { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ref, onValue, off } from 'firebase/database';
import { DrawerPanel } from '../ui/DrawerPanel';
import { ContactPhotoPicker } from '../features/contactPhoto/ContactPhotoPicker';
import { getContactsBySenior, updateSeniorContacts, deletePhoto } from '../../lib/supabase';
import { database } from '../../lib/firebase';
import { API_BASE_URL } from '../../config/apiUrl';
import { syncSeniorConfig } from '../../lib/syncConfig';
import { contactsSyncText, formatSyncAgo } from './contactsSyncText';

/**
 * F04 brief — display phone numbers masked. Show the last 4 digits prefixed
 * with bullets, e.g., (450) 367-9692 → ••• ••• 9692. Edit fields keep the
 * full number so the caregiver can correct typos.
 */
function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '••• ••• ••••';
  return `••• ••• ${digits.slice(-4)}`;
}

const SYNC_FRESH_THRESHOLD_MS = 5 * 60 * 1000;

// Comparable form of the list, to tell whether anything is unsaved. tempId is
// a client-only key; a new contact still differs because it has no id.
function contactsSnapshot(list: Contact[]): string {
  return JSON.stringify(list.map(({ tempId, ...rest }) => rest));
}

interface Contact {
  id?: string;
  tempId?: string;
  name: string;
  phone: string;
  relationship: string;
  is_emergency: boolean;
  photo_url?: string;
  aliases?: string[];
}

interface ContactsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  seniorName: string;
  onContactsUpdated?: () => void;
}

// Photos: tap the photo (or the empty placeholder) to add, change or remove
// it — always through the crop step (features/contactPhoto). Stored files are
// deleted only after a successful save: until then the phone may still be
// showing the old one.

export const ContactsDrawer: FC<ContactsDrawerProps> = ({
  isOpen,
  onClose,
  seniorId,
  seniorName,
  onContactsUpdated,
}) => {
  const { t, i18n } = useTranslation();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  // The list as loaded; null until the first load finishes.
  const [loadedSnapshot, setLoadedSnapshot] = useState<string | null>(null);
  // Photo files uploaded or dropped since the drawer opened; cleaned up on save or close.
  const uploadedPhotos = useRef<Set<string>>(new Set());
  const droppedPhotos = useRef<Set<string>>(new Set());
  // F04 — sync indicator state read from Firebase RTDB. The Android app
  // writes lastContactsSyncAt + contactsCount the moment it applies the
  // new contact list, so "in sync" means the phone really took the change.
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [syncedCount, setSyncedCount] = useState<number | null>(null);
  // Re-render the sync pill every 30 s so "Phone updated · 2m ago" creeps
  // forward without needing a fresh Firebase event.
  const [nowTick, setNowTick] = useState(Date.now());

  // Subscribe to devices/{seniorId}/status/lastContactsSyncAt while the drawer
  // is open. Cleaning up on close means we don't keep an idle listener around
  // for every senior the caregiver has ever browsed to.
  useEffect(() => {
    if (!isOpen || !seniorId) {
      setSyncedAt(null);
      setSyncedCount(null);
      return;
    }
    const statusRef = ref(database, `devices/${seniorId}/status`);
    const handler = onValue(statusRef, (snap) => {
      const v = (snap.val() ?? {}) as { lastContactsSyncAt?: string; contactsCount?: number };
      setSyncedAt(typeof v.lastContactsSyncAt === 'string' ? v.lastContactsSyncAt : null);
      setSyncedCount(typeof v.contactsCount === 'number' ? v.contactsCount : null);
    });
    const tick = window.setInterval(() => setNowTick(Date.now()), 30_000);
    return () => { off(statusRef, 'value', handler); window.clearInterval(tick); };
  }, [isOpen, seniorId]);

  const syncFresh = syncedAt ? (nowTick - new Date(syncedAt).getTime()) < SYNC_FRESH_THRESHOLD_MS : false;

  // The "changes deploy to the phone" note only makes sense once there is
  // something to deploy.
  const hasUnsavedChanges = loadedSnapshot !== null && contactsSnapshot(contacts) !== loadedSnapshot;

  // Fresh photo bookkeeping per open drawer and per senior — not per reload,
  // which also fires on a language change mid-edit.
  useEffect(() => {
    uploadedPhotos.current = new Set();
    droppedPhotos.current = new Set();
  }, [isOpen, seniorId]);

  useEffect(() => {
    if (!isOpen || !seniorId) return;

    const loadContacts = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getContactsBySenior(seniorId);
        const loaded: Contact[] = data.map((c) => ({
          id: c.id,
          name: c.name,
          phone: c.phone,
          relationship: c.relationship,
          is_emergency: c.is_emergency || false,
          photo_url: c.photo_url || undefined,
          aliases: c.aliases || [],
        }));
        setContacts(loaded);
        setLoadedSnapshot(contactsSnapshot(loaded));
      } catch (err) {
        console.error('Failed to load contacts:', err);
        setError(t('drawers.contacts.loadError'));
        setLoadedSnapshot(contactsSnapshot([]));
      } finally {
        setLoading(false);
      }
    };

    loadContacts();
  }, [isOpen, seniorId, t]);

  const handleAdd = () => {
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    setContacts([
      ...contacts,
      { tempId, name: '', phone: '', relationship: '', is_emergency: false, aliases: [] },
    ]);
    setEditingKey(tempId);
  };

  const handleRemove = (contact: Contact) => {
    const key = contact.id || contact.tempId;
    setContacts(contacts.filter((c) => (c.id || c.tempId) !== key));
  };

  const handleField = (index: number, field: keyof Contact, value: string | boolean) => {
    const next = [...contacts];
    next[index] = { ...next[index], [field]: value };
    setContacts(next);
  };

  const handlePhoto = (key: string, url: string | null) => {
    if (url) uploadedPhotos.current.add(url);
    setContacts((prev) =>
      prev.map((c) => {
        if ((c.id || c.tempId) !== key) return c;
        if (c.photo_url) droppedPhotos.current.add(c.photo_url);
        return { ...c, photo_url: url || undefined };
      })
    );
  };

  // Best effort: a leftover file costs storage, never a broken photo on the phone.
  const deletePhotos = (urls: Iterable<string>) => {
    for (const url of Array.from(urls)) deletePhoto(url).catch(() => {});
  };

  const handleClose = () => {
    // Nothing was saved, so nothing on the phone points at this session's uploads.
    deletePhotos(uploadedPhotos.current);
    uploadedPhotos.current = new Set();
    droppedPhotos.current = new Set();
    onClose();
  };

  const handleSave = async () => {
    const valid = contacts.filter((c) => c.name.trim() && c.phone.trim());
    if (valid.length === 0) {
      setError(t('drawers.contacts.validationError'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await updateSeniorContacts(
        seniorId,
        valid.map((c) => ({
          id: c.id,
          name: c.name.trim(),
          phone: c.phone.trim(),
          relationship: c.relationship.trim() || t('drawers.contacts.defaultRelationship'),
          photo_url: c.photo_url || undefined,
          is_emergency: c.is_emergency,
          aliases: c.aliases || [],
        }))
      );

      // Auth + ownership now required server-side; syncSeniorConfig attaches
      // the JWT and never throws (see lib/syncConfig.ts).
      await syncSeniorConfig(seniorId);

      // Saved: replaced or removed photos, and uploads that lost to a later
      // pick, are no longer referenced by any contact.
      const inUse = new Set(valid.map((c) => c.photo_url).filter(Boolean));
      const unused = new Set(
        Array.from(droppedPhotos.current).concat(Array.from(uploadedPhotos.current)).filter((u) => !inUse.has(u))
      );
      uploadedPhotos.current = new Set();
      droppedPhotos.current = new Set();
      deletePhotos(unused);

      onContactsUpdated?.();
      onClose();
    } catch (err) {
      console.error('Failed to update contacts:', err);
      setError(t('drawers.contacts.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerPanel
      isOpen={isOpen}
      onClose={handleClose}
      title={t('drawers.contacts.title')}
      footer={
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3 bg-brand-primary text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
              {t('drawers.contacts.saving')}
            </>
          ) : (
            t('drawers.contacts.save')
          )}
        </button>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <span className="material-symbols-outlined text-3xl text-brand-primary animate-spin">
            progress_activity
          </span>
        </div>
      ) : (
        <>
          {/* F04 brief — "Sync to phone" indicator. Green dot + timestamp
              when the phone reported acceptance of the latest list within
              the last 5 min. Brief explicitly forbids a "blocked count"
              anywhere — this pill carries timing only, no call data.
              Layout uses flex-wrap so the "N on phone" badge drops below
              the timestamp text on narrow drawers instead of being
              truncated. Senior name is dropped (the drawer header already
              names the senior) — keeps the line short enough to fit on
              one row most of the time. */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 rounded-xl border border-brand-border bg-brand-bg-warm/40 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                  syncFresh ? 'bg-emerald-500' : 'bg-brand-text-muted/40'
                }`}
                aria-hidden="true"
              />
              <span className="text-xs text-brand-text-light font-medium">
                {syncedAt
                  ? contactsSyncText(i18n.language, syncFresh ? 'phoneUpdated' : 'lastUpdate', {
                      time: formatSyncAgo(syncedAt, i18n.language, nowTick),
                    })
                  : contactsSyncText(i18n.language, 'waitingForPhone')}
              </span>
            </div>
            {syncedCount !== null && (
              <span className="text-[11px] text-brand-text-muted tabular-nums whitespace-nowrap">
                {contactsSyncText(i18n.language, 'onPhone', { count: syncedCount })}
              </span>
            )}
          </div>

          {contacts.length === 0 ? (
            <div className="text-center py-8 text-brand-text-muted">
              <span className="material-symbols-outlined text-4xl mb-2">contacts</span>
              <p className="text-sm">{t('drawers.contacts.empty')}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {contacts.map((contact, index) => {
                const key = contact.id || contact.tempId || `contact-${index}`;
                const isEditing = editingKey === key;
                return (
                  <div
                    key={key}
                    className="p-3 rounded-xl border border-brand-border bg-brand-bg-warm/40"
                  >
                    {isEditing ? (
                      <div className="flex flex-col gap-2">
                        <div className="flex justify-center pb-1">
                          <ContactPhotoPicker
                            photoUrl={contact.photo_url}
                            contactName={contact.name}
                            onChange={(url) => handlePhoto(key, url)}
                            thumbnailWidth={120}
                          />
                        </div>
                        <input
                          type="text"
                          placeholder={t('drawers.contacts.namePlaceholder')}
                          value={contact.name}
                          onChange={(e) => handleField(index, 'name', e.target.value)}
                          className="px-3 py-2 rounded-lg border border-brand-border bg-white text-sm"
                        />
                        <input
                          type="tel"
                          placeholder={t('drawers.contacts.phonePlaceholder')}
                          value={contact.phone}
                          onChange={(e) => handleField(index, 'phone', e.target.value)}
                          className="px-3 py-2 rounded-lg border border-brand-border bg-white text-sm"
                        />
                        <input
                          type="text"
                          placeholder={t('drawers.contacts.relationshipPlaceholder')}
                          value={contact.relationship}
                          onChange={(e) => handleField(index, 'relationship', e.target.value)}
                          className="px-3 py-2 rounded-lg border border-brand-border bg-white text-sm"
                        />
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={contact.is_emergency}
                            onChange={(e) => handleField(index, 'is_emergency', e.target.checked)}
                            className="accent-brand-primary"
                          />
                          <span>{t('drawers.contacts.emergencyContact')}</span>
                        </label>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingKey(null)}
                            className="flex-1 py-2 text-sm font-semibold text-brand-primary"
                          >
                            {t('drawers.contacts.done')}
                          </button>
                          <button
                            onClick={() => handleRemove(contact)}
                            className="flex-1 py-2 text-sm font-semibold text-red-600"
                          >
                            {t('drawers.contacts.remove')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <ContactPhotoPicker
                          photoUrl={contact.photo_url}
                          contactName={contact.name}
                          onChange={(url) => handlePhoto(key, url)}
                          thumbnailWidth={60}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-sm text-brand-text truncate">
                              {contact.name || t('drawers.contacts.unnamed')}
                            </h4>
                            {contact.is_emergency && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-700 rounded">
                                {t('drawers.contacts.emergencyBadge')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-brand-text-muted truncate">
                            {contact.relationship || t('drawers.contacts.defaultRelationship')} · {contact.phone ? maskPhone(contact.phone) : t('drawers.contacts.noPhone')}
                          </p>
                        </div>
                        <button
                          onClick={() => setEditingKey(key)}
                          aria-label={t('drawers.contacts.editAria')}
                          className="p-1.5 rounded-lg hover:bg-brand-bg-warm text-brand-text-muted"
                        >
                          <span className="material-symbols-outlined text-lg">edit</span>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button
            onClick={handleAdd}
            className="mt-4 w-full py-2.5 rounded-xl border-2 border-dashed border-brand-border text-sm font-semibold text-brand-text-muted hover:border-brand-primary hover:text-brand-primary transition-colors flex items-center justify-center gap-1"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            {t('drawers.contacts.addContact')}
          </button>

          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {hasUnsavedChanges && (
            <p className="text-xs text-brand-text-muted mt-6">
              {t('drawers.contacts.deployNote', { name: seniorName })}
            </p>
          )}
        </>
      )}
    </DrawerPanel>
  );
};

export default ContactsDrawer;
